import { Box, CircularProgress, Dialog, DialogContent, IconButton } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Language } from '@loyalty/shared-common';
import {
  ProfileInteractionPayload,
  renderProfileInteractionDetailHtml,
} from '@loyalty/shared-common/profile-api-client';
import { colorPalette } from '@qualifio/ui-commons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../../shared/api-client/ApiClientProvider';

export interface ProfileInteractionDetailDialogProps {
  profileId: string;
  profileInteractionId: string;
  title?: string;
  onClose: () => void;
}

export function ProfileInteractionDetailDialog({
  profileId,
  profileInteractionId,
  title,
  onClose,
}: ProfileInteractionDetailDialogProps) {
  const { t, i18n } = useTranslation();
  const { profileApi } = useApi();
  const [payload, setPayload] = useState<ProfileInteractionPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setIsError(false);
    setPayload(null);

    profileApi
      .getProfileInteraction(profileId, profileInteractionId)
      .then(data => {
        if (!cancelled) {
          setPayload(data);
        }
      })
      .catch(error => {
        console.error(error);
        if (!cancelled) {
          setIsError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [profileApi, profileId, profileInteractionId]);

  const html = useMemo(
    () => (payload ? renderProfileInteractionDetailHtml(payload, i18n.language as Language) : ''),
    [payload, i18n.language],
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <IconButton
        onClick={onClose}
        aria-label={t('profiles.details.interactions.detail.close')}
        sx={{
          position: 'absolute',
          right: 0,
          zIndex: 1,
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <DialogContent sx={{ textAlign: 'left' }}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 4,
            }}
            role="status"
            aria-live="polite"
            aria-label={t('profiles.details.interactions.detail.loading')}
          >
            <CircularProgress size={32} />
          </Box>
        ) : null}

        {isError ? (
          <Box component="p" role="alert" sx={{ color: colorPalette.grey.mainHover }}>
            {t('profiles.details.interactions.detail.error')}
          </Box>
        ) : null}

        {!isLoading && !isError && !html ? (
          <Box component="p" sx={{ color: colorPalette.grey.mainHover }}>
            {t('profiles.details.interactions.detail.empty')}
          </Box>
        ) : null}

        {!isLoading && !isError && html ? (
          <Box
            sx={{ overflow: 'auto', textAlign: 'left' }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
